import React, { useEffect } from "react"
import { useAlert, useAuth, useTheme } from "hooks"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { isNotEmpty } from "utils"
import { updateOrderBillingInformation } from "features/bikeLabelOrder/slice/bikeLabelOrderSlice"
import { DataOperators } from "models"
import { getBilingFormInformationSelector } from "features/bikeLabelOrder/slice/bikeLabelOrderSelectors"
import { shallowEqual } from "utils/functions"
import { BC_OPERATOR } from "utils/constants"
import { privateRoutes } from "router/routes"
import { getBicycodeInfoFromPartner, isStoreChainAuthorized } from "utils/bikeLabel"
import { editEntityOperators } from "features/Partner/api/PartnerApi"

export const useBicyCodeBilingOperator = () => {
	const { entity, fetchEntity, isBCOperator } = useAuth()
	const theme = useTheme()
	const dispatch = useAppDispatch()
	const billingInformationForm = useAppSelector(getBilingFormInformationSelector)
	const operatorBicyCode = entity.data_operators
		? getBicycodeInfoFromPartner(entity.data_operators)
		: false
	const { t } = useTranslation()
	const { alert } = useAlert()
	const navigate = useNavigate()

	const entityStoreChains = entity?.entity_data?.code_chaine

	useEffect(() => {
		if (
			!(
				isStoreChainAuthorized(entityStoreChains) &&
				theme.currentTheme.id_api === BC_OPERATOR
			)
		) {
			navigate(privateRoutes.bike.basePath)
		}
	}, [entityStoreChains, navigate, isBCOperator, theme.currentTheme.id_api])

	const checkBillingConsistency = React.useCallback(() => {
		const { invoicing_agent_firstname, invoicing_agent_lastname, invoicing_agent_mail } = (
			operatorBicyCode as DataOperators
		).entity_data
		return shallowEqual(billingInformationForm, {
			invoicing_agent_firstname,
			invoicing_agent_lastname,
			invoicing_agent_mail,
		})
	}, [operatorBicyCode, billingInformationForm])

	const buildEntityData = (billingInformationForm, operatorBicyCode) => {
		const { invoicing_agent_mail, invoicing_agent_lastname, invoicing_agent_firstname } =
			billingInformationForm
		const { entity_data } = operatorBicyCode

		return {
			invoicing_agent_mail: invoicing_agent_mail.trim(),
			invoicing_agent_lastname,
			invoicing_agent_firstname,
			...(isNotEmpty(entity_data.is_member_fub) && {
				is_member_fub: entity_data.is_member_fub,
			}),
			...(isNotEmpty(entity_data.operator_store_id) && {
				operator_store_id: entity_data.operator_store_id,
			}),
			...(isNotEmpty(entity_data.operator_gender) && {
				operator_gender: entity_data.operator_gender,
			}),
		}
	}

	const updateRequestBilingInformation = React.useCallback(async () => {
		try {
			const entityData = buildEntityData(billingInformationForm, operatorBicyCode)

			const dataOperators = [
				{
					operator: BC_OPERATOR,
					status: (operatorBicyCode as DataOperators).status,
					entity_data: entityData,
				},
			]

			const updatedOperators = {
				entity_id: entity.entity_id,
				last_row_hash: entity.row_hash,
				data_operators: dataOperators,
			}

			const { getError } = await editEntityOperators(updatedOperators)

			if (getError()) throw getError()
			await fetchEntity(entity.entity_id)

			alert("success", t("edit_success", { ns: "partner" }))
			return true
		} catch (error) {
			const errorMsg = (error as unknown as Error)?.message?.toString()
			if (errorMsg) alert("error", errorMsg)
			return false
		}
	}, [
		billingInformationForm,
		operatorBicyCode,
		entity.entity_id,
		entity.row_hash,
		fetchEntity,
		alert,
		t,
	])

	React.useEffect(() => {
		if (operatorBicyCode && "entity_data" in operatorBicyCode) {
			dispatch(
				updateOrderBillingInformation({
					sepaInProgress: isNotEmpty(
						operatorBicyCode?.entity_data?.effective_date
					) as boolean,
					invoicing_agent_mail: isNotEmpty(
						operatorBicyCode.entity_data.invoicing_agent_mail as string
					)
						? (operatorBicyCode?.entity_data?.invoicing_agent_mail as string)
						: "",
					invoicing_agent_lastname: isNotEmpty(
						operatorBicyCode.entity_data.invoicing_agent_lastname
					)
						? operatorBicyCode.entity_data.invoicing_agent_lastname
						: "",
					invoicing_agent_firstname: isNotEmpty(
						operatorBicyCode.entity_data.invoicing_agent_firstname
					)
						? operatorBicyCode.entity_data.invoicing_agent_firstname
						: "",
				})
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [operatorBicyCode])

	return { checkBillingConsistency, updateRequestBilingInformation, entityStoreChains }
}
